import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Trophy, AlertTriangle, XCircle } from 'lucide-react';

const DIMENSION_LABELS = {
  accountability: 'Accountability',
  integrity: 'Integrity',
  problem_solving: 'Problem Solving',
  emotional_maturity: 'Emotional Maturity',
  social_empathy: 'Social & Empathy',
  motivation_values: 'Motivation & Values',
  leadership: 'Leadership',
};

const CANDIDATE_COLORS = [
  { fill: 'rgba(127,119,221,0.18)', stroke: 'var(--brand)', dot: 'var(--brand)', bg: 'bg-brand' },
  { fill: 'rgba(29,158,117,0.18)', stroke: '#1D9E75', dot: '#1D9E75', bg: 'bg-[#1D9E75]' },
  { fill: 'rgba(239,159,39,0.18)', stroke: '#EF9F27', dot: '#EF9F27', bg: 'bg-[#EF9F27]' },
  { fill: 'rgba(226,75,74,0.18)', stroke: '#E24B4A', dot: '#E24B4A', bg: 'bg-[#E24B4A]' },
  { fill: 'rgba(56,132,244,0.18)', stroke: '#3884F4', dot: '#3884F4', bg: 'bg-[#3884F4]' },
  { fill: 'rgba(168,85,247,0.18)', stroke: '#A855F7', dot: '#A855F7', bg: 'bg-[#A855F7]' },
];

const OverlayRadarChart = ({ candidates, size = 380 }) => {
  const dims = Object.keys(DIMENSION_LABELS);
  const n = dims.length;
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.34;
  const angleStep = (2 * Math.PI) / n;

  const getPoint = (index, value) => {
    const angle = angleStep * index - Math.PI / 2;
    const dist = (value / 100) * r;
    return { x: cx + dist * Math.cos(angle), y: cy + dist * Math.sin(angle) };
  };

  const gridLevels = [20, 40, 60, 80, 100];

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mx-auto">
      {gridLevels.map((level) => {
        const points = dims.map((_, i) => getPoint(i, level));
        const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
        return <path key={level} d={d} fill="none" stroke="#e5e7eb" strokeWidth="1" />;
      })}
      {dims.map((_, i) => {
        const end = getPoint(i, 100);
        return <line key={i} x1={cx} y1={cy} x2={end.x} y2={end.y} stroke="#e5e7eb" strokeWidth="1" />;
      })}
      {gridLevels.filter(l => l % 40 === 0).map(level => (
        <text key={`lbl-${level}`} x={cx + 4} y={cy - (level / 100) * r + 3} className="text-[9px] fill-neutral-400" style={{ fontFamily: 'Manrope, sans-serif' }}>{level}</text>
      ))}
      {candidates.map((c, ci) => {
        const color = CANDIDATE_COLORS[ci % CANDIDATE_COLORS.length];
        const scores = c.dimension_scores || {};
        const dataPoints = dims.map((dim, i) => getPoint(i, scores[dim] || 0));
        const pathData = dataPoints.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ') + ' Z';
        return (
          <g key={ci}>
            <path d={pathData} fill={color.fill} stroke={color.stroke} strokeWidth="2.5" />
            {dataPoints.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="4" fill={color.dot} stroke="white" strokeWidth="1.5" />
            ))}
          </g>
        );
      })}
      {dims.map((dim, i) => {
        const labelPoint = getPoint(i, 120);
        return (
          <text key={dim} x={labelPoint.x} y={labelPoint.y} textAnchor="middle" dominantBaseline="middle"
            className="text-[10px] fill-neutral-600 font-medium" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {DIMENSION_LABELS[dim]}
          </text>
        );
      })}
    </svg>
  );
};

const VerdictIcon = ({ verdict }) => {
  if (verdict === 'STRONG HIRE') return <Trophy className="h-4 w-4 text-emerald-600" />;
  if (verdict === 'CONDITIONAL HIRE') return <AlertTriangle className="h-4 w-4 text-amber-600" />;
  if (verdict === 'DO NOT HIRE') return <XCircle className="h-4 w-4 text-red-600" />;
  return null;
};

const CandidateCompare = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ids = searchParams.get('ids');
    if (!ids) { navigate('/dashboard'); return; }
    loadComparison(ids.split(','));
  }, [searchParams]);

  const loadComparison = async (ids) => {
    try {
      const res = await api.compareCandidates(ids);
      setCandidates(res.data.candidates);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load comparison');
      navigate('/dashboard');
    } finally { setLoading(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F6]">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" />
    </div>
  );

  const dims = Object.keys(DIMENSION_LABELS);
  const bestPerDim = {};
  dims.forEach(dim => {
    let best = -1, bestIdx = -1;
    candidates.forEach((c, i) => {
      const s = c.dimension_scores?.[dim] || 0;
      if (s > best) { best = s; bestIdx = i; }
    });
    bestPerDim[dim] = bestIdx;
  });

  return (
    <div className="min-h-screen bg-[#F9F9F6] py-6 px-4" data-testid="compare-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-start mb-6">
          <div>
            <Button onClick={() => navigate('/dashboard')} variant="outline" className="rounded-md mb-3" data-testid="back-to-dashboard">
              <ArrowLeft className="h-4 w-4 mr-2" /> Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-neutral-900" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="compare-title">
              Candidate Comparison
            </h1>
            <p className="text-sm text-neutral-500 mt-1">Comparing {candidates.length} candidates side by side</p>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-3 mb-6">
          {candidates.map((c, i) => (
            <div key={i} className="flex items-center gap-2 bg-white border border-neutral-200 rounded-lg px-3 py-2" data-testid={`legend-${i}`}>
              <span className={`w-3 h-3 rounded-full ${CANDIDATE_COLORS[i % CANDIDATE_COLORS.length].bg}`} />
              <span className="text-sm font-medium text-neutral-800">{c.candidate_name}</span>
              <span className="text-xs text-neutral-500">{c.role_applied_for}</span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Radar Chart */}
          <div className="bg-white rounded-lg border border-neutral-200 p-6">
            <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Overlay Radar</h2>
            <p className="text-xs text-neutral-500 mb-4">All dimensions compared on a single chart</p>
            <OverlayRadarChart candidates={candidates} />
          </div>

          {/* Summary Cards */}
          <div className="space-y-3">
            {candidates.map((c, i) => {
              const color = CANDIDATE_COLORS[i % CANDIDATE_COLORS.length];
              const avgScore = dims.length > 0
                ? Math.round(dims.reduce((sum, d) => sum + (c.dimension_scores?.[d] || 0), 0) / dims.length)
                : 0;
              return (
                <div key={i} className="bg-white rounded-lg border border-neutral-200 p-4" data-testid={`summary-card-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-3 h-3 rounded-full" style={{ backgroundColor: color.stroke }} />
                      <span className="font-semibold text-neutral-900">{c.candidate_name}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <VerdictIcon verdict={c.recommendation} />
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                        c.recommendation === 'STRONG HIRE' ? 'bg-emerald-100 text-emerald-800' :
                        c.recommendation === 'CONDITIONAL HIRE' ? 'bg-amber-100 text-amber-800' :
                        c.recommendation === 'DO NOT HIRE' ? 'bg-red-100 text-red-800' : 'bg-neutral-100 text-neutral-600'
                      }`}>{c.recommendation}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-center">
                    <div className="p-2 bg-neutral-50 rounded-md">
                      <p className="text-[10px] text-neutral-500 uppercase tracking-wide">Avg Score</p>
                      <p className="text-lg font-bold text-neutral-900">{avgScore}</p>
                    </div>
                    <div className="p-2 bg-neutral-50 rounded-md">
                      <p className="text-[10px] text-neutral-500 uppercase tracking-wide">Consistency</p>
                      <p className={`text-lg font-bold ${c.consistency_score < 60 ? 'text-red-600' : 'text-neutral-900'}`}>{c.consistency_score}%</p>
                    </div>
                    <div className="p-2 bg-neutral-50 rounded-md">
                      <p className="text-[10px] text-neutral-500 uppercase tracking-wide">Avg Time</p>
                      <p className="text-lg font-bold text-neutral-900">{c.avg_response_time}s</p>
                    </div>
                    <div className="p-2 bg-neutral-50 rounded-md">
                      <p className="text-[10px] text-neutral-500 uppercase tracking-wide">Red Flags</p>
                      <p className={`text-lg font-bold ${c.red_flag_count > 0 ? 'text-red-600' : 'text-neutral-900'}`}>{c.red_flag_count}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Dimension-by-Dimension Table */}
        <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
          <div className="p-6 border-b border-neutral-100">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>Dimension Breakdown</h2>
            <p className="text-xs text-neutral-500 mt-1">Highest score per dimension highlighted</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="dimension-table">
              <thead>
                <tr className="border-b border-neutral-100 bg-neutral-50">
                  <th className="text-left text-xs font-bold text-neutral-500 uppercase tracking-wider px-6 py-3">Dimension</th>
                  {candidates.map((c, i) => (
                    <th key={i} className="text-center text-xs font-bold uppercase tracking-wider px-4 py-3" style={{ color: CANDIDATE_COLORS[i % CANDIDATE_COLORS.length].stroke }}>
                      {c.candidate_name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dims.map((dim, di) => (
                  <tr key={dim} className={di % 2 === 0 ? 'bg-white' : 'bg-neutral-50/50'}>
                    <td className="px-6 py-3 text-sm font-medium text-neutral-700">{DIMENSION_LABELS[dim]}</td>
                    {candidates.map((c, ci) => {
                      const score = c.dimension_scores?.[dim] || 0;
                      const isBest = bestPerDim[dim] === ci;
                      const color = score >= 80 ? 'text-emerald-700' : score >= 60 ? 'text-neutral-900' : score >= 40 ? 'text-amber-700' : 'text-red-700';
                      return (
                        <td key={ci} className="text-center px-4 py-3">
                          <span className={`text-sm font-bold ${color} ${isBest ? 'relative' : ''}`}>
                            {score}
                            {isBest && <span className="absolute -top-1 -right-3 text-[9px] text-emerald-500 font-bold">*</span>}
                          </span>
                          <div className="mt-1 mx-auto w-16 h-1 bg-neutral-100 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: CANDIDATE_COLORS[ci % CANDIDATE_COLORS.length].stroke }} />
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* Average Row */}
                <tr className="border-t-2 border-neutral-200 bg-neutral-50 font-bold">
                  <td className="px-6 py-3 text-sm font-bold text-neutral-900">Average</td>
                  {candidates.map((c, ci) => {
                    const avg = dims.length > 0
                      ? Math.round(dims.reduce((s, d) => s + (c.dimension_scores?.[d] || 0), 0) / dims.length)
                      : 0;
                    return (
                      <td key={ci} className="text-center px-4 py-3 text-sm font-bold" style={{ color: CANDIDATE_COLORS[ci % CANDIDATE_COLORS.length].stroke }}>
                        {avg}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CandidateCompare;
